import { memo, useEffect, useState } from "react"

import { get, set } from "lodash/fp"

import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import { CellProps } from "components/TreeState"
import { ChangedPlanRecord, PlanRecordType } from "../types"
import { HourRangePicker } from "components/HourPicker/HourRangePicker"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"

export const HourIntervalCell = memo(function _HourIntervalCell(
  props: CellProps,
) {
  const { rowId: id, dataKey, columnId, disabled } = props
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const writeTo = tableSource.state
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const [isDisabled, setIsDisabled] = useState<boolean>(false)

  const action = readFrom[id]?.action
  const updatable = !action || action === "UPDATE_ORDER"
  const type = readFrom[id]?.type as PlanRecordType
  const error = get(`${id}.${columnId}`, readError)
  const buyingStatus = readFrom[id]?.buyingStatus

  const start = String(get(`${dataKey}.start`, readFrom[id]) || "")
  const end = String(get(`${dataKey}.end`, readFrom[id]) || "")

  const onChange = ({ name, value }: { name: string; value?: string }) => {
    tableSource.clearError(id)
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    writeTo[id] = set(`${dataKey}.${name}`, value, writeTo[id])
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  const handleBlur = () => {
    autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
  }

  const hide = type !== "line_item"

  useEffect(() => {
    buyingStatus === "POSTED_FOR_BILLING" && setIsDisabled(true)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyingStatus])

  return hide ? (
    <span />
  ) : (
    <Bulky {...props}>
      <div>
        <HourRangePicker
          disabled={disabled || isDisabled}
          start={start}
          end={end}
          onChange={onChange}
          size="small"
          onBlur={handleBlur}
        />
        {error ? <CellError error={error} /> : null}
      </div>
    </Bulky>
  )
})
